import { Injectable } from "@angular/core";
import { cloneDeep } from "lodash";
import { RevisionCompareUrlService } from "../revision-compare-url/revision-compare-url.service";

@Injectable()
export class DeployHelperService {
  constructor(private revisionCompareUrlService: RevisionCompareUrlService) {}

  public calculateDeployAge(deploy: any, nextDeploy: any) {
    const currentCreatedAt = new Date(deploy.createdAt).getTime();
    const nextCreatedAt = nextDeploy
      ? new Date(nextDeploy.createdAt).getTime()
      : // The most recent deploy has no next deploy and uses the current time.
        new Date().getTime();

    const milliseconds = nextCreatedAt - currentCreatedAt;
    const minutes = milliseconds / (1000 * 60);

    return minutes;
  }

  public calculateOccurrenceRate(noticeCount: any, time: any) {
    // Make sure occurrence rate is not infinity
    const rate = noticeCount / time;
    if (!isFinite(rate)) {
      return 0;
    }
    return rate;
  }

  public processDeploys(deploys: any) {
    deploys = this.attachPreviousDeployRevisions(deploys);
    deploys = this.attachRedeployFlag(deploys);
    deploys = this.attachLinkUrls(deploys);
    return this.attachOccurrenceRate(deploys);
  }

  // Determine the previously deployed revision for each deploy, where
  // possible. This allows us to link to a diff, showing what was deployed.
  public attachPreviousDeployRevisions(deploys: any) {
    const revisionsByEnv: any = {};

    return cloneDeep(deploys)
      .reverse()
      .map((d: any) => {
        d.previousRevision = revisionsByEnv[d.environment];
        revisionsByEnv[d.environment] = d.revision;
        return d;
      })
      .reverse();
  }

  public attachRedeployFlag(deploys: any) {
    return cloneDeep(deploys).map((d: any) => {
      d.redeploy = d.previousRevision === d.revision;
      return d;
    });
  }

  public attachLinkUrls(deploys: any) {
    return cloneDeep(deploys).map((d: any) => {
      d.linkUrl = this.revisionCompareUrlService.for(
        d.repository,
        d.previousRevision,
        d.revision,
      );
      return d;
    });
  }

  public attachOccurrenceRate(rawDeploys: any[]) {
    // Sort by descending age. We need this because the deploy age is the
    // difference in creation time between each deploy
    // Copy the deploys to preserve the original order.

    const deployMap: any = {};

    cloneDeep(rawDeploys)
      .sort((a: any, b: any) => {
        return (
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
      })
      .map((d: any, _i: number, deploys: any) => {
        const deploysWithSameEnv = deploys.filter((deploy: any) => {
          return d.environment === deploy.environment;
        });
        const nextDeploy =
          deploysWithSameEnv[deploysWithSameEnv.indexOf(d) - 1];
        const deployAge =
          d.deployAge != null
            ? d.deployAge
            : this.calculateDeployAge(d, nextDeploy);
        deployMap[d.id] = {
          deployAge,
          occurrenceRate: this.calculateOccurrenceRate(
            d.noticeTotalCount,
            deployAge,
          ),
        };
        return d;
      });

    return cloneDeep(rawDeploys).map((d: any) => {
      d.deployAge = deployMap[d.id].deployAge;
      d.occurrenceRate = deployMap[d.id].occurrenceRate;
      return d;
    });
  }
}
